import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Download } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { POLYGONSCAN_BASE_URL } from '@shared/constants/blockchain';
import ProviderBadge, { isStripeProvider } from './ProviderBadge';
import DateRangeSelector, { getFilterDates } from './DateRangeSelector';
import type { DateFilter } from './DateRangeSelector';
import { timeAgo } from './timeAgo';
import { downloadCsv } from './csvExport';
import { useAuth } from '../hooks/useAuth';
import StripeBalanceCards from '../stripe/StripeBalanceCards';
import StripeBankPayouts from '../stripe/StripeBankPayouts';
import StripeTransferHistory from '../stripe/StripeTransferHistory';
import type { Order } from '@shared/types';

function isCryptoOrder(o: Order) {
  return o.provider === 'crypto' || o.payment_provider === 'crypto';
}

interface Props {
  solution?: 'crypto' | 'stripe' | 'square';
}

function StripePayoutsView() {
  const { user } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasStripeAccount, setHasStripeAccount] = useState(true);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('merchants')
      .select('api_key, stripe_account_id')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.api_key) setApiKey(data.api_key);
        if (!data?.stripe_account_id) setHasStripeAccount(false);
        setLoading(false);
      });
  }, [user]);

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Stripe Payouts</h1>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading...</p>
      </div>
    );
  }

  if (!hasStripeAccount || !apiKey) {
    return (
      <div className="p-6">
        <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Stripe Payouts</h1>
        <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <div className="text-3xl mb-3 opacity-20">$</div>
          <p className="font-sans text-sm mb-2" style={{ color: 'var(--dash-text-muted)' }}>Connect your Stripe account to view payout data</p>
          <Link
            to="/dashboard/settings"
            className="inline-block px-4 py-2 rounded font-mono text-xs transition-colors"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-tertiary)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
          >
            Go to Settings
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Stripe Payouts</h1>
      <StripeBalanceCards apiKey={apiKey} />
      <StripeBankPayouts apiKey={apiKey} />
      <StripeTransferHistory apiKey={apiKey} />
    </div>
  );
}

export default function Payouts({ solution }: Props) {
  if (solution === 'stripe') {
    return <StripePayoutsView />;
  }

  return <CryptoPayoutsView solution={solution} />;
}

function CryptoPayoutsView({ solution }: Props) {
  const [payouts, setPayouts] = useState<Order[]>([]);
  const [refunds, setRefunds] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ range: '30d' });

  const label = solution === 'crypto' ? 'Crypto Payouts' : 'Payouts';

  useEffect(() => {
    async function load() {
      const { start, end } = getFilterDates(dateFilter);
      let payoutsQuery = supabase
        .from('orders')
        .select('*')
        .eq('status', 'completed')
        .order('completed_at', { ascending: false })
        .limit(200);

      let refundsQuery = supabase
        .from('orders')
        .select('*')
        .eq('status', 'refunded')
        .order('updated_at', { ascending: false })
        .limit(200);

      if (start) {
        payoutsQuery = payoutsQuery.gte('completed_at', start);
        refundsQuery = refundsQuery.gte('updated_at', start);
      }
      if (end) {
        payoutsQuery = payoutsQuery.lte('completed_at', end);
        refundsQuery = refundsQuery.lte('updated_at', end);
      }

      const [payoutsRes, refundsRes] = await Promise.all([payoutsQuery, refundsQuery]);

      if (payoutsRes.data) {
        let result = payoutsRes.data as unknown as Order[];
        if (solution === 'crypto') result = result.filter(isCryptoOrder);
        setPayouts(result);
      }
      if (refundsRes.data) {
        let result = refundsRes.data as unknown as Order[];
        if (solution === 'crypto') result = result.filter(isCryptoOrder);
        setRefunds(result);
      }
      setLoading(false);
    }
    load();
  }, [solution, dateFilter]);

  const totalVolume = payouts.reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);
  const totalRefunded = refunds.reduce((sum, o) => sum + o.expected_amount, 0);
  const netVolume = totalVolume - totalRefunded;
  const avgPayout = payouts.length > 0 ? totalVolume / payouts.length : 0;

  const handleExportCsv = () => {
    const headers = ['Order ID', 'Amount', 'Provider', 'TX', 'Settled'];
    const rows = payouts.map((o) => [
      o.id,
      (o.verified_amount || o.expected_amount).toFixed(2),
      o.provider || o.payment_provider || '',
      o.txid || '',
      o.completed_at ? new Date(o.completed_at).toISOString() : '',
    ]);
    downloadCsv(headers, rows, `payouts-${solution || 'all'}-${dateFilter.range}`);
  };

  const LARGE_THRESHOLD = avgPayout * 3;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>{label}</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCsv}
            disabled={payouts.length === 0}
            title="Export CSV"
            className="p-1.5 rounded transition-colors disabled:opacity-40"
            style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; }}
          >
            <Download size={14} />
          </button>
          <DateRangeSelector value={dateFilter} onChange={setDateFilter} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="rounded p-4" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <div className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>Settled Volume</div>
          <div className="font-mono text-xl" style={{ color: 'var(--dash-text-primary)' }}>${totalVolume.toFixed(2)}</div>
        </div>
        <div className="rounded p-4" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <div className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>Net Volume</div>
          <div className="font-mono text-xl text-emerald-400">${netVolume.toFixed(2)}</div>
        </div>
        <div className="rounded p-4" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <div className="font-mono text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--dash-text-dim)' }}>Avg Payout</div>
          <div className="font-mono text-xl" style={{ color: 'var(--dash-text-primary)' }}>${avgPayout.toFixed(2)}</div>
        </div>
        {totalRefunded > 0 && (
          <div className="border border-purple-500/20 rounded bg-purple-500/5 p-4">
            <div className="font-mono text-[10px] text-purple-400 uppercase tracking-widest mb-1">Refunded</div>
            <div className="font-mono text-xl text-purple-300">-${totalRefunded.toFixed(2)}</div>
          </div>
        )}
      </div>

      <div className="font-mono text-xs mb-3" style={{ color: 'var(--dash-text-dim)' }}>
        {payouts.length} settlement{payouts.length !== 1 ? 's' : ''}
      </div>

      {loading ? (
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading payouts...</p>
      ) : payouts.length === 0 ? (
        <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <div className="text-3xl mb-3 opacity-30">{'\u039E'}</div>
          <p className="font-sans text-sm mb-1" style={{ color: 'var(--dash-text-muted)' }}>No payouts yet</p>
          <p className="font-sans text-xs" style={{ color: 'var(--dash-text-faint)' }}>
            Settlements will appear here after your first successful transaction.
          </p>
        </div>
      ) : (
        <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
          <table className="w-full text-sm font-sans">
            <thead>
              <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Order</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Amount</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Type</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Settlement</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>TX</th>
                <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Settled</th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((order, i) => {
                const amount = order.verified_amount || order.expected_amount;
                const isLarge = LARGE_THRESHOLD > 0 && amount >= LARGE_THRESHOLD;
                return (
                  <tr
                    key={order.id}
                    className="transition-colors"
                    style={{
                      background: isLarge ? 'rgba(16, 185, 129, 0.05)' : i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)',
                      borderBottom: '1px solid var(--dash-border-light)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isLarge ? 'rgba(16, 185, 129, 0.05)' : i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                      {order.id.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                      ${amount.toFixed(2)}
                      {isLarge && (
                        <span className="ml-1.5 text-emerald-400 text-[9px]">HIGH</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <ProviderBadge provider={order.provider} paymentProvider={order.payment_provider} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>
                      {isStripeProvider(order.provider, order.payment_provider)
                        ? 'Stripe Connect'
                        : order.merchant_wallet
                          ? `${order.merchant_wallet.slice(0, 6)}...${order.merchant_wallet.slice(-4)}`
                          : '--'}
                    </td>
                    <td className="px-4 py-2.5">
                      {isStripeProvider(order.provider, order.payment_provider) ? (
                        <span className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>Settled via Stripe</span>
                      ) : order.txid ? (
                        <a
                          href={`${POLYGONSCAN_BASE_URL}/tx/${order.txid}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-xs inline-flex items-center gap-1 transition-colors"
                          style={{ color: 'var(--dash-text-tertiary)' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-primary)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                        >
                          {order.txid.slice(0, 10)}...
                          <span className="text-[10px]">{'\u2197'}</span>
                        </a>
                      ) : (
                        <span className="font-mono text-xs" style={{ color: 'var(--dash-text-faint)' }}>--</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                      {order.completed_at ? timeAgo(order.completed_at) : '--'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {refunds.length > 0 && (
        <div className="mt-8">
          <h2 className="font-mono text-[10px] uppercase tracking-widest mb-3" style={{ color: 'var(--dash-text-dim)' }}>
            Refunds ({refunds.length})
          </h2>
          <div className="rounded overflow-x-auto" style={{ border: '1px solid var(--dash-border)' }}>
            <table className="w-full text-sm font-sans">
              <thead>
                <tr style={{ background: 'var(--dash-card)', borderBottom: '1px solid var(--dash-border)' }}>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Order</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Amount</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Type</th>
                  <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-widest font-medium" style={{ color: 'var(--dash-text-dim)' }}>Refund Date</th>
                </tr>
              </thead>
              <tbody>
                {refunds.map((order, i) => (
                  <tr
                    key={order.id}
                    className="transition-colors"
                    style={{ background: i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)', borderBottom: '1px solid var(--dash-border-light)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = i % 2 === 0 ? 'var(--dash-row-even)' : 'var(--dash-row-odd)'; }}
                  >
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>
                      {order.id.slice(0, 8)}...
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-purple-300">
                      -${order.expected_amount.toFixed(2)}
                    </td>
                    <td className="px-4 py-2.5">
                      <ProviderBadge provider={order.provider} paymentProvider={order.payment_provider} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>
                      {timeAgo(order.updated_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
